import { Dispatch, MutableRefObject, useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

import { Action, DrawingActionKind, OverlayType, Snapshot, State, isPolygon } from './types';
import { DrawingController } from './use-drawing-manager';

export default function reducer(state: State, action: Action) {
  switch (action.type) {
    case DrawingActionKind.UPDATE_OVERLAYS: {
      const overlay = state.now;

      if (!overlay) {
        return state;
      }

      const { geometry } = overlay;

      if (isPolygon(geometry)) {
        overlay.snapshot.path = geometry.getPath()?.getArray();
      }

      return {
        now: { ...overlay },
      };
    }

    case DrawingActionKind.SET_OVERLAY: {
      const { overlay } = action.payload;

      const snapshot: Snapshot = {};

      if (isPolygon(overlay)) {
        const path = overlay.getPath()?.getArray();
        snapshot.path = path;
      }

      return {
        now: {
          type: action.payload.type,
          geometry: action.payload.overlay,
          snapshot,
        },
      };
    }
    case DrawingActionKind.CLEAR_OVERLAY:
      return {};
    default:
      return { ...state };
  }
}

function addPolygonListeners(
  polygon: google.maps.Polygon,
  overlaysShouldUpdateRef: MutableRefObject<boolean>,
  dispatch: Dispatch<Action>,
): google.maps.MapsEventListener[] {
  const mouseupListener = google.maps.event.addListener(polygon, 'mouseup', () => {
    if (overlaysShouldUpdateRef.current) {
      dispatch({ type: DrawingActionKind.UPDATE_OVERLAYS });
    }
  });

  const dragstartListener = google.maps.event.addListener(polygon, 'dragstart', () => {
    overlaysShouldUpdateRef.current = false;
  });

  const dragendListener = google.maps.event.addListener(polygon, 'dragend', () => {
    overlaysShouldUpdateRef.current = true;
    dispatch({ type: DrawingActionKind.UPDATE_OVERLAYS });
  });

  return [mouseupListener, dragstartListener, dragendListener];
}

export function useDrawingManagerEvents(
  drawingController: DrawingController | null,
  overlaysShouldUpdateRef: MutableRefObject<boolean>,
  dispatch: Dispatch<Action>,
  savedCoordinates: string | null,
) {
  const map = useMap();
  const polygonListenersRef = useRef<google.maps.MapsEventListener[]>([]);

  useEffect(() => {
    if (!drawingController) return;

    const unsubscribe = drawingController.onPolygonComplete(polygon => {
      // Remove listeners from any previously drawn polygon
      polygonListenersRef.current.forEach(l => google.maps.event.removeListener(l));

      polygonListenersRef.current = addPolygonListeners(polygon, overlaysShouldUpdateRef, dispatch);

      dispatch({
        type: DrawingActionKind.SET_OVERLAY,
        payload: { type: OverlayType.POLYGON, overlay: polygon },
      });
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch, drawingController, overlaysShouldUpdateRef]);

  useEffect(() => {
    if (!map || !savedCoordinates) return;

    const coordinates = JSON.parse(savedCoordinates) as { lat: number; lng: number }[] | null;

    if (!coordinates) return;

    const polygon = new google.maps.Polygon({
      paths: coordinates,
      map,
      editable: true,
      draggable: true,
    });

    polygonListenersRef.current = addPolygonListeners(polygon, overlaysShouldUpdateRef, dispatch);

    dispatch({
      type: DrawingActionKind.SET_OVERLAY,
      payload: { type: OverlayType.POLYGON, overlay: polygon },
    });

    return () => {
      polygon.setMap(null);
      polygonListenersRef.current.forEach(l => google.maps.event.removeListener(l));
      polygonListenersRef.current = [];
    };
  }, [map, savedCoordinates]);

  useEffect(() => {
    return () => {
      polygonListenersRef.current.forEach(l => google.maps.event.removeListener(l));
      polygonListenersRef.current = [];
    };
  }, []);
}

export function useOverlaySnapshots(
  map: google.maps.Map | null,
  state: State,
  overlaysShouldUpdateRef: MutableRefObject<boolean>,
) {
  useEffect(() => {
    if (!map || !state.now) return;

    const overlay = state.now;

    overlaysShouldUpdateRef.current = false;
    overlay.geometry.setMap(map);

    const { path } = overlay.snapshot;
    if (isPolygon(overlay.geometry)) {
      overlay.geometry.setPath(path ?? []);
    }

    overlaysShouldUpdateRef.current = true;

    return () => {
      const current = state.now;
      if (current) {
        current.geometry.setMap(null);
      }
    };
  }, [map, overlaysShouldUpdateRef, state.now]);
}
